import {
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import { useForm, Controller, type SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import {
  FaArrowLeft,
  FaCloudUploadAlt,
  FaPaperPlane,
  FaDesktop,
  FaCommentDots,
  FaPaperclip,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useSendHelpContact } from "../../api/mutations/useSendHelpContact";
import { useAppContext } from "../../Context/AppContext";
import {
  HELP_CATEGORIES,
  getHelpScreenKeysForMembership,
  type HelpCategoryId,
} from "./helpCategories";

type CategoryFormValues = {
  screen: string;
  message: string;
};

type CategoryFormProps = {
  categoryId: HelpCategoryId;
  onBack: () => void;
};

function getInputClassName(hasError: boolean, base = "form-control rounded-3") {
  return `${base} ${hasError ? "is-invalid" : ""}`.trim();
}

function CategoryForm({ categoryId, onBack }: CategoryFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAppContext();
  const [submitError, setSubmitError] = useState("");
  const [attachment, setAttachment] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const category = HELP_CATEGORIES.find((c) => c.id === categoryId);
  const showScreenField =
    categoryId === "bug" || categoryId === "improvement";

  const screenKeys = useMemo(
    () => getHelpScreenKeysForMembership(user?.user?.membershipName),
    [user?.user?.membershipName]
  );

  const schema = useMemo(
    () =>
      yup.object({
        screen: yup
          .string()
          .default("")
          .test(
            "screen-required",
            t("help.validation.screenRequired"),
            (value) => !showScreenField || Boolean(value?.trim())
          ),
        message: yup
          .string()
          .min(10, t("help.validation.messageMin"))
          .required(t("help.validation.messageRequired")),
      }),
    [showScreenField, t]
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CategoryFormValues>({
    resolver: yupResolver(schema),
    defaultValues: { screen: "", message: "" },
  });

  const onSuccess = useCallback(() => {
    navigate("/contact-confirmation");
  }, [navigate]);

  const onError = useCallback(() => {
    setSubmitError(t("help.submitError"));
  }, [t]);

  const { mutate: sendHelpContact, isPending } = useSendHelpContact({
    onSuccess,
    onError,
  });

  const messageLabel =
    categoryId === "bug"
      ? t("help.form.describeBug")
      : categoryId === "improvement"
        ? t("help.form.describeIdea")
        : categoryId === "collaboration"
          ? t("help.form.describeCollaboration")
          : t("help.form.describeHelp");

  const onSubmit: SubmitHandler<CategoryFormValues> = useCallback(
    (data) => {
      setSubmitError("");
      const categoryTitle = category
        ? t(category.titleKey)
        : t("help.hub.title");

      sendHelpContact({
        category: categoryTitle,
        categoryId,
        message: data.message,
        ...(showScreenField && data.screen ? { screen: data.screen } : {}),
        file: attachment,
      });
    },
    [attachment, category, categoryId, sendHelpContact, showScreenField, t]
  );

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setAttachment(e.target.files?.[0] ?? null);
  }, []);

  return (
    <div className="card shadow border-0 rounded-4 overflow-hidden">
      <div className="card-header bg-project-gradient text-white border-0 px-4 py-4">
        <div className="d-flex align-items-start gap-3">
          <button
            type="button"
            className="btn btn-outline-light rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 p-2"
            onClick={onBack}
            aria-label={t("help.back")}
          >
            <FaArrowLeft />
          </button>
          <div className="flex-grow-1">
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
              <span className="d-inline-flex align-items-center gap-2 rounded-pill border border-white border-opacity-25 bg-white bg-opacity-10 text-white small px-3 py-1">
                <span className="opacity-75 text-uppercase fw-semibold">
                  {t("help.form.step")}
                </span>
              </span>
              <span className="d-inline-flex align-items-center rounded-pill border border-white border-opacity-25 bg-white bg-opacity-10 text-white small px-3 py-1 fw-semibold">
                {category ? t(category.titleKey) : t("help.hub.title")}
              </span>
            </div>
            <h3 className="mb-1 fw-bold">
              {category ? t(category.titleKey) : t("help.hub.title")}
            </h3>
            {category && (
              <p className="mb-0 opacity-75 small">{t(category.descKey)}</p>
            )}
          </div>
        </div>
      </div>

      <div className="card-body bg-body-tertiary p-3 p-md-4">
        {submitError && (
          <div className="alert alert-danger rounded-4">{submitError}</div>
        )}

        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          className="bg-white border-0 shadow-sm rounded-4 p-3 p-md-4"
        >
          {showScreenField && (
            <div className="mb-4">
              <div className="d-flex align-items-center gap-2 mb-2">
                <FaDesktop className="text-dark" size={14} aria-hidden />
                <label
                  htmlFor="help-screen"
                  className="form-label fw-semibold mb-0 text-dark"
                >
                  {t("help.form.whichScreen")}
                </label>
              </div>
              <Controller
                name="screen"
                control={control}
                render={({ field }) => (
                  <select
                    id="help-screen"
                    className={getInputClassName(
                      !!errors.screen,
                      "form-select form-select-lg rounded-3"
                    )}
                    {...field}
                  >
                    <option value="">{t("help.form.selectScreen")}</option>
                    {screenKeys.map((key) => {
                      const label = t(key);
                      return (
                        <option key={key} value={label}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                )}
              />
              {errors.screen && (
                <div className="invalid-feedback d-block">
                  {errors.screen.message}
                </div>
              )}
            </div>
          )}

          <div className="mb-4">
            <div className="d-flex align-items-center gap-2 mb-2">
              <FaCommentDots className="text-dark" size={14} aria-hidden />
              <label
                htmlFor="help-message"
                className="form-label fw-semibold mb-0 text-dark"
              >
                {messageLabel}
              </label>
            </div>
            <Controller
              name="message"
              control={control}
              render={({ field }) => (
                <textarea
                  id="help-message"
                  className={getInputClassName(
                    !!errors.message,
                    "form-control rounded-3"
                  )}
                  rows={6}
                  placeholder={t("help.form.messagePlaceholder")}
                  {...field}
                />
              )}
            />
            {errors.message && (
              <div className="invalid-feedback d-block">
                {errors.message.message}
              </div>
            )}
          </div>

          <div className="mb-4">
            <div className="d-flex align-items-center gap-2 mb-2">
              <FaPaperclip className="text-dark" size={14} aria-hidden />
              <label className="form-label fw-semibold mb-0 text-dark">
                {t("help.form.attachOptional")}
              </label>
            </div>
            <div className="border border-2 border-dashed rounded-4 p-4 text-center bg-body-tertiary">
              <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-body-secondary text-dark p-3 mb-2">
                <FaCloudUploadAlt size={24} aria-hidden />
              </div>
              <p className="fw-semibold mb-1">
                {attachment
                  ? t("help.form.fileReady")
                  : t("help.form.uploadTitle")}
              </p>
              <p className="small text-muted mb-3 text-truncate px-2">
                {attachment ? attachment.name : t("help.form.attachHint")}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                className="d-none"
                accept="image/*,video/*,.pdf"
                onChange={handleFileChange}
              />
              <button
                type="button"
                className="btn btn-outline-primary rounded-pill px-4"
                onClick={() => fileInputRef.current?.click()}
              >
                {attachment
                  ? t("help.form.changeFile")
                  : t("help.form.uploadFile")}
              </button>
            </div>
          </div>

          <div className="d-grid gap-2">
            <button
              type="submit"
              className="btn btn-primary btn-lg rounded-pill fw-semibold d-inline-flex align-items-center justify-content-center gap-2"
              disabled={isPending}
            >
              <FaPaperPlane aria-hidden />
              {isPending ? t("help.sending") : t("help.submit")}
            </button>
          </div>
          <p className="text-center text-muted small mt-3 mb-0">
            {t("help.responseExpectation")}
          </p>
        </form>
      </div>
    </div>
  );
}

CategoryForm.displayName = "CategoryForm";

export default memo(CategoryForm);
